import type { HttpPort } from "~/lib/http";
import type { WeekInfo } from "~/features/teacher/WeekBanner";

export interface TodayStudent {
  student_id: string;
  first_name: string;
  last_name: string;
  daily_goal?: string | null;
  daily_task_text?: string | null;
  monster_mood?: string | null;
  monster_mood_custom?: string | null;
  task_skipped?: boolean;
  checkin_time?: string | null;
  checkout_time?: string | null;
  mood?: number | null;
  morning_mood?: number | null;
  goal_1_rating?: number | null;
  daily_task_score?: number | null;
  daily_task_comment?: string | null;
  class_goal_rating?: number | null;
  is_absent?: boolean;
  mood_chips?: Array<{ id: string; emoji: string; label: string }> | null;
}

export interface TodayOverview {
  date: string;
  checked_in_morning: TodayStudent[];
  checked_out_evening: TodayStudent[];
  not_checked_in: TodayStudent[];
  total_students: number;
  morning_count: number;
  evening_count: number;
  active_goals: Array<{ rank: number | null; title: string }>;
  class_daily_goal: string | null;
  voting_status: {
    has_active_voting: boolean;
    status?: "open" | "closed" | string;
    session_id?: string;
    participation_count?: number;
    total_students?: number;
  };
}

export interface ClassGoal {
  id: string;
  class_id: string;
  goal_date: string;
  goal_text: string;
}

export interface ClassGoalResults {
  goal_text: string | null;
  avg_rating: number | null;
  total_ratings: number;
  total_students: number;
  participation_percent: number;
}

export interface YesterdayResults {
  date: string;
  goal_text: string | null;
  avg_rating: number | null;
  avg_mood: number | null;
  checkout_count: number;
  total_students: number;
  has_data: boolean;
}

export interface TreeData {
  tree_level: number;
  tree_points: number;
  points_to_next_level: number;
  progress_percent: number;
  total_checkins: number;
  total_checkouts: number;
  last_week: null | {
    week_start: string;
    week_end: string;
    points_earned: number;
    level_reached: number;
  };
}

export interface TodaySnapshot {
  overview: TodayOverview;
  weekInfo: WeekInfo | null;
  classGoal: ClassGoal | null;
  classGoalResults: ClassGoalResults | null;
  yesterday: YesterdayResults | null;
  tree: TreeData | null;
}

export function createTodayModule(http: HttpPort, token: string) {
  const optional = async <T>(path: string): Promise<T | null> => {
    try {
      return await http.request<T>({ path, token });
    } catch {
      return null;
    }
  };

  return {
    async load(classId: string): Promise<TodaySnapshot> {
      const [overview, weekInfo, goalPayload, classGoalResults, yesterday, tree] = await Promise.all([
        http.request<TodayOverview>({ path: `/classes/${classId}/today`, token }),
        optional<WeekInfo>(`/classes/${classId}/week-info`),
        optional<{ goal: ClassGoal | null }>(`/class-goals/${classId}/today`),
        optional<ClassGoalResults>(`/class-goals/${classId}/results/today`),
        optional<YesterdayResults>(`/class-goals/${classId}/results/yesterday`),
        optional<TreeData>(`/classes/${classId}/tree`)
      ]);
      return {
        overview,
        weekInfo,
        classGoal: goalPayload?.goal ?? null,
        classGoalResults,
        yesterday,
        tree
      };
    },

    async saveClassGoal(classId: string, goalText: string): Promise<void> {
      await http.request({ path: `/class-goals/${classId}`, method: "POST", token, body: { goal_text: goalText } });
    },

    async resetClassGoal(classId: string): Promise<void> {
      await http.request({ path: `/class-goals/${classId}/today`, method: "DELETE", token });
    },

    async resetStudentToday(classId: string, studentId: string): Promise<void> {
      await http.request({ path: `/checkins/${classId}/reset-today/${studentId}`, method: "DELETE", token });
    },

    async resetVoting(classId: string): Promise<void> {
      const current = await http.request<{ session?: { id: string } | null }>({ path: `/voting/${classId}/current`, token });
      if (!current.session?.id) throw new Error("Keine aktive Abstimmung gefunden.");
      await http.request({ path: `/voting/${classId}/reset/${current.session.id}`, method: "DELETE", token });
    }
  };
}

export type TodayModule = ReturnType<typeof createTodayModule>;
